import { type PropsWithChildren, useCallback, useEffect, useState } from 'react';
import unionBy from 'lodash/unionBy';
import { useLocation } from 'react-router';
import {
  addOrUpdateUserPreference,
  fetchUserPreferenceThrowingServiceUnavailable,
} from '@common/api/rest';
import { fetchNewFeatureConfiguration } from '@common/api/webResources';
import NewFeaturePopup from '@common/components/GuidedTourPopup/NewFeaturePopup';
import {
  getAllNewFeatures,
  getVersionNewFeatures,
  getVersions,
  type NewFeature,
} from '@common/newFeatures';
import { useAppSelector } from '@common/store';
import { type TourStep } from '@common/types/guidedTour';
import { UserState } from '@common/types/store';
import compareVersions from '@common/utils/device/compareVersions';
import {
  USER_PREFERENCE_SW_LAST_VERSION,
  USER_PREFERENCE_SW_NEW_FEATURES,
} from '@common/utils/storage/storageKeys';

const currentVersion = `${process.env.APP_VERSION}`;
const minNumberOfFeaturesToShowAsSinglePopup = 5;

type NewFeatureWithDetails = NewFeature & TourStep;

function NewFeatureContainer({ children }: Readonly<Required<PropsWithChildren<{}>>>) {
  const currentUser = useAppSelector((state) => state.main.user);
  const userState = useAppSelector((store) => store.main.userState);
  const { pathname } = useLocation();
  const [newFeatures, setNewFeatures] = useState<NewFeature[] | undefined>();
  const [visibleNewFeatures, setVisibleNewFeatures] = useState<
    NewFeatureWithDetails[] | undefined
  >();

  useEffect(() => {
    const loadNewFeatures = async () => {
      const [previousVersion, unseenFeaturesIds = []] = await Promise.all([
        fetchUserPreferenceThrowingServiceUnavailable<string>(USER_PREFERENCE_SW_LAST_VERSION),
        fetchUserPreferenceThrowingServiceUnavailable<string[]>(USER_PREFERENCE_SW_NEW_FEATURES),
      ]);

      if (previousVersion === currentVersion && unseenFeaturesIds.length === 0) {
        return;
      }

      let newVersionUnseenFeatures: NewFeature[] = [];
      const allFeatures = getAllNewFeatures(currentUser?.userLevel);
      if (!previousVersion) {
        newVersionUnseenFeatures = allFeatures;
      } else if (compareVersions(previousVersion, currentVersion) > 0) {
        newVersionUnseenFeatures = getVersions().reduce<NewFeature[]>(
          (result, version) => {
            if (
              compareVersions(currentVersion, version) <= 0
              && compareVersions(previousVersion, version) > 0
            ) {
              return [
                ...result,
                ...getVersionNewFeatures(version, currentUser?.userLevel),
              ];
            }
            return result;
          },
          [],
        );
      }

      const pendingUnseenFeatures = unseenFeaturesIds.length > 0
        ? allFeatures.filter((feature) => unseenFeaturesIds.includes(feature.id))
        : [];

      setNewFeatures(
        unionBy(newVersionUnseenFeatures, pendingUnseenFeatures, (feature) => feature.id),
      );

      if (previousVersion !== currentVersion) {
        addOrUpdateUserPreference(USER_PREFERENCE_SW_LAST_VERSION, currentVersion);
      }
    };
    if (userState === UserState.LoggedIn) {
      void loadNewFeatures()
        .catch(() => {
          // If Error 503 is received, just catch it
        });
    }
  }, [currentUser?.userLevel, userState]);

  useEffect(() => {
    if (newFeatures) {
      const newFeatureIds = newFeatures.map((newFeature) => newFeature.id);
      addOrUpdateUserPreference(USER_PREFERENCE_SW_NEW_FEATURES, newFeatureIds);
    }
  }, [newFeatures]);

  useEffect(() => {
    if (newFeatures) {
      const prepareFeaturesForDisplay = async (features: NewFeature[]) => {
        try {
          const details = await Promise.all(
            features.map((feature) => fetchNewFeatureConfiguration(feature.id)),
          );
          setVisibleNewFeatures(features.map((feature, index) => ({
            ...feature,
            ...details[index],
          })));
        } catch {
          console.log('Unable to load new feature config', features);
        }
      };

      if (newFeatures.length < minNumberOfFeaturesToShowAsSinglePopup) {
        const matchedNewFeatures = newFeatures.filter(({ path }) => path.exec(pathname));
        if (matchedNewFeatures.length) {
          prepareFeaturesForDisplay(matchedNewFeatures);
        }
      } else {
        prepareFeaturesForDisplay(newFeatures);
      }
    }
  }, [pathname, newFeatures]);

  const markNewFeatureSeen = useCallback(() => {
    if (visibleNewFeatures) {
      const visibleNewFeatureIds = visibleNewFeatures.map(({ id }) => id);
      setNewFeatures((prevNewFeatures = []) =>
        prevNewFeatures.filter((newFeature) => !visibleNewFeatureIds.includes(newFeature.id))
      );
      setVisibleNewFeatures(undefined);
    }
  }, [visibleNewFeatures]);

  return (
    <>
      {children}
      {visibleNewFeatures && (
        <NewFeaturePopup steps={visibleNewFeatures} onClose={markNewFeatureSeen} />
      )}
    </>
  );
}

export default NewFeatureContainer;
